import { fetchGlobal } from "../../../../../api/hooks";
import { PROJECT_KEY } from "../constants";
import {
    POSConfig,
    POSSalesModeItem,
    POSPaymentModeItem,
    POSLanguageItem,
    POSInformationModeItem,
    POSDisplayName,
    RawGeneralSettingItem,
    DEFAULT_POS_CONFIG,
    PosConfigLevel,
} from "./constants";

const API_BASE = process.env.REACT_APP_API_V2_URL;

interface ShopSettings {
    setting_sale_mode?: RawGeneralSettingItem[];
    setting_language?: RawGeneralSettingItem[];
    setting_Payment_Method?: RawGeneralSettingItem[];
    setting_Information_mode?: RawGeneralSettingItem[];
    setting_Logo?: Record<string, unknown>;
}

interface GeneralsDataResponse {
    data: Record<string, ShopSettings>;
    error?: string;
}

interface SaveConfigParams {
    userId: string;
    franchiseId: string;
    shopId: string;
    projectId: string;
    config: POSConfig;
    configLevel: PosConfigLevel | string;
    propagateToChildren?: boolean;
    appId?: string;
    keyApplication?: string;
    operatorId?: string;
    uuidUser?: string;
}

interface SaveConfigResponse {
    error: boolean;
    message: string;
}

function buildDisplayName(
    langs: Record<string, { name?: string }> | undefined,
    fallback: string
): POSDisplayName {
    return {
        FR: langs?.fr?.name || fallback,
        EN: langs?.en?.name || fallback,
        ES: langs?.es?.name || "",
        IT: langs?.it?.name || "",
        NL: langs?.nl?.name || "",
        PT: langs?.pt?.name || "",
    };
}

export async function fetchPosGeneralSettings(
    franchiseId: string,
    shopId: string,
    _userId?: string
): Promise<POSConfig> {
    try {
        const queryString = new URLSearchParams({
            franchiseId,
            shopId,
        }).toString();

        const data = await fetchGlobal<GeneralsDataResponse>(
            `${API_BASE}/settings/generals/dataPos?${queryString}`,
            {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            }
        );

        if (!data?.data) {
            return DEFAULT_POS_CONFIG;
        }

        const shopSettings = data.data[shopId] || data.data["0"] || {};

        return {
            SalesMode: {
                items: mapActiveModes<POSSalesModeItem>(shopSettings.setting_sale_mode || []),
            },
            PaymentMode: {
                skipStep: false,
                items: mapActiveModes<POSPaymentModeItem>(
                    shopSettings.setting_Payment_Method || []
                ),
            },
            Language: {
                items: mapLanguages(shopSettings.setting_language || []),
            },
            InformationMode: {
                skipStep: false,
                items: mapInformationModes(
                    shopSettings.setting_Information_mode || []
                ),
            },
            PaperlessTicket: DEFAULT_POS_CONFIG.PaperlessTicket,
        };
    } catch (err) {
        // Re-throw so the thunk's rejected case fires and the UI toast
        // in the slice surfaces a real error instead of falling back
        // silently to DEFAULT_POS_CONFIG.
        const message =
            err instanceof Error
                ? err.message
                : "Failed to load POS general settings";
        throw new Error(message);
    }
}

function mapActiveModes<T extends POSSalesModeItem | POSPaymentModeItem>(
    items: RawGeneralSettingItem[]
): T[] {
    return items
        .filter(
            (item) => item.active === true || item.active === undefined
        )
        .map((item) => ({
            id: item.id,
            name: item.name || item.shortName || "",
            active: item.active !== false,
            isFavorite: item.isFavorite === true,
            displayName: buildDisplayName(item.languages, item.name || ""),
        })) as T[];
}

function mapLanguages(languages: RawGeneralSettingItem[]): POSLanguageItem[] {
    return languages.map((item) => ({
        id: item.id,
        name: item.name || item.language || "",
        flag: item.flag || "",
        title: item.title || item.name || "",
        active: item.active !== false,
        isFavorite: item.isFavorite === true || item.isDefault === true,
        isDefault: item.isDefault === true,
    }));
}

function mapInformationModes(
    informationModes: RawGeneralSettingItem[]
): POSInformationModeItem[] {
    return informationModes.map((item) => {
        let active: Record<string, boolean>;
        if (
            typeof item.active === "object" &&
            item.active !== null &&
            !Array.isArray(item.active)
        ) {
            active = { ...(item.active as Record<string, boolean>) };
        } else if (typeof item.active === "boolean") {
            active = { default: item.active };
        } else {
            active = {};
        }

        return {
            id: item.id,
            name: item.name || "",
            active,
            isFavorite: item.isFavorite === true,
            displayName: buildDisplayName(item.languages, item.name || ""),
        };
    });
}

export interface PosItemDisable {
    id: string;
    state: string | null;
    shopId: string | null;
}

interface ItemsDisableResponse {
    error?: boolean;
    data?: PosItemDisable[];
}

export async function fetchPosItemsDisable(
    franchiseId: string,
    shopId: string
): Promise<PosItemDisable[]> {
    if (!franchiseId) return [];
    try {
        const queryString = new URLSearchParams({
            franchiseId,
            shopId: shopId || "0",
        }).toString();

        const data = await fetchGlobal<ItemsDisableResponse>(
            `${API_BASE}/settings/consomation/items?${queryString}`,
            {
                method: "GET",
                headers: { "Content-Type": "application/json" },
            }
        );

        return Array.isArray(data?.data) ? data!.data! : [];
    } catch {
        return [];
    }
}

export async function savePosConfigToServer(
    params: SaveConfigParams
): Promise<SaveConfigResponse> {
    const {
        userId,
        franchiseId,
        shopId,
        projectId,
        config,
        configLevel,
        propagateToChildren,
        appId,
        keyApplication,
        operatorId,
        uuidUser,
    } = params;

    return fetchGlobal<SaveConfigResponse>(
        `${API_BASE}/settings/application/pos/project/configuration`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userId,
                franchiseId,
                shopId,
                projectId,
                project_id: PROJECT_KEY,
                configLevel,
                appId: appId || "",
                keyApplication: keyApplication || "",
                propagateToChildren: propagateToChildren || false,
                operatorId: operatorId || "",
                uuidUser: uuidUser || "",
                data: {
                    posConfiguration: config,
                },
            }),
        }
    );
}
